import React, { useState } from 'react';
import { manualTrailerApi } from '../api';

export default function ManageTrailers({ subAccountId, trackers, onTrailersChanged }) {
  const [showForm, setShowForm] = useState(false);
  const [editingTrailer, setEditingTrailer] = useState(null);
  const [linkingTrailer, setLinkingTrailer] = useState(null);
  const [selectedRidedogId, setSelectedRidedogId] = useState('');
  const [saving, setSaving] = useState(false);
  const [linking, setLinking] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });

  const manualTrailers = trackers.filter(t => t.manual);
  const ridedogTrailers = trackers.filter(t => !t.manual);

  const openNewForm = () => {
    setEditingTrailer(null);
    setForm({ name: '', description: '' });
    setShowForm(true);
  };

  const openEditForm = (trailer) => {
    setEditingTrailer(trailer);
    setForm({ name: trailer.name, description: trailer.description || '' });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingTrailer) {
        await manualTrailerApi.update(subAccountId, editingTrailer.id, form.name, form.description);
      } else {
        await manualTrailerApi.add(subAccountId, form.name, form.description);
      }
      setShowForm(false);
      onTrailersChanged();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (trailer) => {
    if (!window.confirm(`Remove "${trailer.name}"? This will also delete its service log entries.`)) return;
    await manualTrailerApi.remove(subAccountId, trailer.id).catch(() => {});
    onTrailersChanged();
  };

  const handleLink = async (trailer) => {
    if (!selectedRidedogId) { alert('Please select a GPS tracker.'); return; }
    const ridedogTracker = ridedogTrailers.find(t => t.id === selectedRidedogId);
    if (!window.confirm(
      `Connect "${trailer.name}" to GPS tracker "${ridedogTracker?.name}"?\n\nGPS data from "${ridedogTracker?.name}" will be associated with "${trailer.name}". Your service log history stays with "${trailer.name}".`
    )) return;
    setLinking(true);
    try {
      await manualTrailerApi.link(subAccountId, trailer.id, selectedRidedogId, ridedogTracker?.name);
      setLinkingTrailer(null);
      setSelectedRidedogId('');
      onTrailersChanged();
    } catch (err) {
      alert('Failed to link: ' + err.message);
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async (trailer) => {
    if (!window.confirm(`Disconnect "${trailer.name}" from its GPS tracker? GPS data will no longer be associated.`)) return;
    try {
      await manualTrailerApi.unlink(subAccountId, trailer.id);
      onTrailersChanged();
    } catch (err) {
      alert('Failed to unlink: ' + err.message);
    }
  };

  return (
    <div className="alerts-panel">
      <div className="alerts-header">
        <div>
          <h2>Manage Trailers</h2>
          <p>Your trailers from RentFlow. Connect each one to its GPS tracker.</p>
        </div>
        <button className="btn-primary" onClick={openNewForm}>+ Add Trailer</button>
      </div>

      {showForm && (
        <div className="alert-form-card">
          <h3>{editingTrailer ? 'Edit Trailer' : 'Add Trailer'}</h3>
          <form onSubmit={handleSave}>
            <div className="form-row">
              <div className="form-group">
                <label>Trailer name</label>
                <input type="text" value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. B6" required autoFocus />
              </div>
              <div className="form-group">
                <label>Description <span className="field-hint">(optional)</span></label>
                <input type="text" value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="e.g. 2021 PJ Car Hauler" />
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save Trailer'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Trackers are deliberately NOT listed here.
          A bare list of tracker names is not something an owner acts on - what
          they need is which of THEIR trailers has GPS and which does not, which
          the badge on each row already says. The trackers themselves now only
          appear in the connect dropdown, at the moment they are being chosen.
          `ridedogTrailers` is still derived above: it feeds that dropdown and
          the "Connected to X" badge lookup. */}

      {/* Manual trailers */}
      <div>
        <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--slate-400)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
          Your RentFlow Trailers
        </div>
        {/* Covers the fleet-sync gap: a trailer created while its tenant's sync
            was failing never reached RideDog, and re-saving it in RentFlow is
            what forces the resync. Worth removing once that is fixed properly. */}
        <div style={{
          background: 'var(--slate-50)', border: '1px solid var(--slate-200)',
          borderRadius: '8px', padding: '10px 12px', marginBottom: '12px',
          fontSize: '12.5px', color: 'var(--slate-600)', lineHeight: '1.5',
        }}>
          <strong>Don't see one of your trailers?</strong> Open it in RentFlow and hit
          Save — it will appear here within a minute. Then use <strong>Connect tracker</strong> to
          pair it with its GPS tracker.
        </div>
        {manualTrailers.length === 0 && !showForm ? (
          <div className="empty-state">
            <span className="empty-icon">🚛</span>
            <h3>No trailers yet</h3>
            <p>Trailers you save in RentFlow appear here automatically. If one is missing, open it in RentFlow and hit Save.</p>
            <button className="btn-primary" onClick={openNewForm}>Add First Trailer</button>
          </div>
        ) : (
          <div className="alerts-list">
            {manualTrailers.map(t => {
              const linkedTracker = ridedogTrailers.find(r => r.id === t.linked_ridedog_id);
              return (
                <div key={t.id} className="alert-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div className="alert-row-main">
                      <div className="alert-tracker-name">{t.name}</div>
                      {t.description && <div className="alert-meta">{t.description}</div>}
                      <div style={{ marginTop: '4px', display: 'flex', gap: '6px', alignItems: 'center' }}>
                        {linkedTracker ? (
                          <span style={{ background: '#dcfce7', color: '#16a34a', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>
                            Connected to {linkedTracker.name}
                          </span>
                        ) : (
                          <span style={{ background: 'var(--slate-100)', color: 'var(--slate-500)', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>
                            Not connected
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="alert-row-actions" style={{ flexShrink: 0 }}>
                      {linkedTracker ? (
                        <button className="btn-ghost" style={{ fontSize: '12px', padding: '5px 10px' }}
                          onClick={() => handleUnlink(t)}>
                          Disconnect
                        </button>
                      ) : ridedogTrailers.length > 0 && (
                        <button className="btn-ghost" style={{ fontSize: '12px', padding: '5px 10px', whiteSpace: 'nowrap' }}
                          onClick={() => { setLinkingTrailer(t.id === linkingTrailer ? null : t.id); setSelectedRidedogId(''); }}>
                          Connect tracker
                        </button>
                      )}
                      <button className="btn-icon" onClick={() => openEditForm(t)} title="Edit">✏️</button>
                      <button className="btn-icon danger" onClick={() => handleDelete(t)} title="Delete">🗑</button>
                    </div>
                  </div>

                  {/* Link panel */}
                  {linkingTrailer === t.id && (
                    <div style={{
                      background: 'var(--slate-50)', border: '1px solid var(--slate-200)',
                      borderRadius: '8px', padding: '14px', display: 'flex', gap: '10px', alignItems: 'flex-end',
                    }}>
                      <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                        <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--slate-600)', marginBottom: '5px', display: 'block' }}>
                          Select a GPS tracker to connect
                        </label>
                        <select value={selectedRidedogId} onChange={e => setSelectedRidedogId(e.target.value)}
                          style={{ padding: '8px 10px', border: '1px solid var(--slate-200)', borderRadius: '6px', fontSize: '13px', width: '100%' }}>
                          <option value="">Select a tracker...</option>
                          {ridedogTrailers.map(r => {
                            // Already on another trailer? Say so and don't let it
                            // be picked. Disconnect it there first — one tracker
                            // on two trailers splits that trailer's mileage.
                            const takenBy = manualTrailers.find(m => m.linked_ridedog_id === r.id && m.id !== t.id);
                            return (
                              <option key={r.id} value={r.id} disabled={!!takenBy}>
                                {r.name}{takenBy ? ` — already on ${takenBy.name}` : ''}
                              </option>
                            );
                          })}
                        </select>
                        <p style={{ fontSize: '11px', color: 'var(--slate-400)', marginTop: '5px' }}>
                          GPS data from the selected tracker will be associated with this trailer. Your service log history stays here.
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                        <button className="btn-ghost" onClick={() => setLinkingTrailer(null)}>Cancel</button>
                        <button className="btn-primary" onClick={() => handleLink(t)} disabled={!selectedRidedogId || linking}>
                          {linking ? 'Connecting...' : 'Connect'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
